import React, { useEffect, useState } from 'react';
import { apiBaseUrl, services } from '../data';
import { parseApiResponse } from '../utils/api';
import './ApplicationForm.css';

const initialForm = {
  fullName: '',
  email: '',
  phone: '',
  alternatePhone: '',
  service: '',
  experience: '',
  relevantExperience: '',
  currentSkill: '',
  newSkill: '',
  candidatePassword: '',
  location: '',
  lastWorkingDay: '',
  noticePeriod: '',
  currentCompany: '',
  previousCompanies: '',
  currentCtc: '',
  expectedCtc: '',
  preferredLocation: '',
  employmentGaps: '',
  uanNumber: '',
  comments: '',
  consultantName: '',
  role: '',
  jobDescription: '',
  companyName: '',
  timings: '',
  duration: '',
  examName: '',
  examCode: '',
  examDate: '',
  examTime: '',
  interviewDate: '',
  interviewTime: '',
  yearPassedOut: '',
  internStartDate: '',
  internEndDate: '',
  payrollStartDate: '',
  payrollEndDate: '',
  panNumber: '',
  dateOfBirth: '',
  permanentAddress: '',
  gender: '',
  qualification: '',
  offerDate: '',
  joiningDate: '',
  hikeDate: '',
  joiningCtc: '',
  joiningDesignation: '',
  currentDesignation: '',
  resignationDate: '',
  relievingDate: '',
  technologyCourseName: '',
  bankName: '',
  bankAccountNumber: '',
  hasPostGraduation: '',
  postGraduationType: '',
  postGraduationCourse: '',
  postGraduationPassoutYear: '',
  postGraduationScore: '',
  graduationType: '',
  graduationCourse: '',
  graduationPassoutYear: '',
  graduationScore: '',
  graduation: '',
  twelfthType: '',
  twelfthPassoutYear: '',
  twelfthScore: '',
  twelfthStandard: '',
  sscPassoutYear: '',
  sscScore: '',
  ssc: '',
  educationGaps: '',
  skillsTrained: '',
};

const serviceFormDetails = {
  'Freshers Job Support': {
    heading: 'Freshers Job Support Form',
    intro: 'Share your education, training, preferred location, and the kind of first role you want us to support.',
  },
  'Lateral Job Support': {
    heading: 'Lateral Job Support Form',
    intro: 'Tell us about your current experience, preferred technology, target role, and package expectations.',
  },
  'Open Job Support': {
    heading: 'Open Job Support Form',
    intro: 'Share your background so we can map you to the right training and current market openings.',
  },
  'Technical Job Support': {
    heading: 'Technical Job Support Form',
    intro: 'Describe the technical area where you need expert support in your current job or project.',
  },
  'Certification Support': {
    heading: 'Certification Support Form',
    intro: 'Tell us which certification you are targeting and when you plan to complete it.',
  },
  'Internship Support': {
    heading: 'Internship Support Form',
    intro: 'Share your academic background, preferred internship area, and availability.',
  },
  'Interview Support': {
    heading: 'Interview Support Form',
    intro: 'Tell us about the interview round, company type, and topics where you need preparation support.',
  },
  'Training Support': {
    heading: 'Training Support Form',
    intro: 'Tell us which skills you want to learn and the kind of role you want the training to prepare you for.',
  },
  'Payroll Support': {
    heading: 'Payroll Support Form',
    intro: 'Share payroll onboarding details and required documents for processing.',
  },
  'Documentation Support': {
    heading: 'Documentation Support Form',
    intro: 'Share documentation, joining, bank, UAN, PAN, Aadhaar, and consultant details for processing.',
  },
};

const graduationCourses = {
  'B.Tech / B.E': [
    'Computer Science Engineering',
    'Information Technology',
    'Electronics and Communication',
    'Electrical and Electronics',
    'Mechanical Engineering',
    'Civil Engineering',
    'Artificial Intelligence and Data Science',
  ],
  Degree: [
    'B.Sc Computer Science',
    'B.Sc Mathematics',
    'B.Sc Statistics',
    'B.Com Computers',
    'B.Com General',
    'BBA',
    'BA',
  ],
  BCA: [
    'Computer Applications',
    'Data Science',
    'Cloud Computing',
    'Cyber Security',
  ],
  Diploma: [
    'Computer Engineering',
    'Electronics and Communication',
    'Electrical Engineering',
    'Mechanical Engineering',
    'Civil Engineering',
  ],
  Pharmacy: [
    'B.Pharmacy',
    'Pharm.D',
  ],
  Other: [
    'Other / Not Listed',
  ],
};

const postGraduationCourses = {
  'M.Tech / M.E': [
    'Computer Science Engineering',
    'Data Science',
    'Artificial Intelligence',
    'VLSI Design',
    'Embedded Systems',
    'Structural Engineering',
  ],
  MBA: [
    'Finance',
    'Marketing',
    'Human Resources',
    'Business Analytics',
    'Operations',
    'Information Systems',
  ],
  MCA: [
    'Computer Applications',
    'Data Science',
    'Cloud Computing',
    'Cyber Security',
  ],
  'M.Sc': [
    'Computer Science',
    'Information Technology',
    'Mathematics',
    'Statistics',
    'Data Science',
  ],
  'M.Com': [
    'Accounting and Finance',
    'Banking and Insurance',
    'Taxation',
    'Business Studies',
  ],
  Other: [
    'Other / Not Listed',
  ],
};

const experienceOptions = [
  'Fresher',
  '0-1 years',
  '1-2 years',
  '2-3 years',
  '3-4 years',
  '4-5 years',
  '5+ years',
  '7+ years',
  '10+ years',
];

const twelfthOptions = [
  'Intermediate / 12th',
  'Diploma',
  'PUC',
  'CBSE 12th',
  'ICSE / ISC',
  'State Board',
  'NIOS',
  'Other',
];

const passoutYears = Array.from({ length: 31 }, (_item, index) => String(2030 - index));

const buildEducationValue = (parts) => parts.filter(Boolean).join(' - ');

const supportFormFields = {
  'Lateral Job Support': [
    { label: 'Name', name: 'fullName', required: true },
    { label: 'Mobile', name: 'phone', type: 'tel', required: true },
    { label: 'Alternate Mobile', name: 'alternatePhone', type: 'tel', required: true },
    { label: 'Email ID', name: 'email', type: 'email', required: true },
    { label: 'Password', name: 'candidatePassword', type: 'password' },
    { label: 'Location', name: 'location', required: true },
    { label: 'Total Experience', name: 'experience', type: 'select', options: experienceOptions, required: true },
    { label: 'Relevant Experience', name: 'relevantExperience', type: 'select', options: experienceOptions.filter((experience) => experience !== 'Fresher'), required: true },
    { label: 'Skill', name: 'skillsTrained', required: true },
    { label: 'Last Working Day', name: 'lastWorkingDay', type: 'date', required: true },
    { label: 'Notice Period', name: 'noticePeriod', required: true },
    { label: 'Current Company Name', name: 'currentCompany', required: true },
    { label: 'Previous Companies Name', name: 'previousCompanies', required: true },
    { label: 'Current CTC', name: 'currentCtc', required: true },
    { label: 'Expected CTC', name: 'expectedCtc', required: true },
    { label: 'Preferred Location', name: 'preferredLocation', required: true },
    { label: 'Employment Gaps', name: 'employmentGaps', required: true },
    { label: 'Resume', name: 'resume', type: 'file', accept: '.pdf,.doc,.docx', responseKey: 'resume_file', required: true },
    { label: 'Aadhaar', name: 'aadhaarCard', type: 'file', accept: '.pdf,.jpg,.jpeg,.png', responseKey: 'aadhaar_card_file', required: true },
    { label: 'PAN Card', name: 'panCard', type: 'file', accept: '.pdf,.jpg,.jpeg,.png', responseKey: 'pan_card_file', required: true },
    { label: 'UAN No', name: 'uanNumber', required: true },
    { label: 'UAN Passbook', name: 'uanPassbook', type: 'file', accept: '.pdf,.jpg,.jpeg,.png', responseKey: 'uan_passbook_file', required: true },
    { label: 'Comments', name: 'comments', type: 'textarea' },
    { label: 'Consultant Name', name: 'consultantName', required: true },
  ],
  'Open Job Support': [
    { label: 'Name', name: 'fullName', required: true },
    { label: 'Mobile', name: 'phone', type: 'tel', required: true },
    { label: 'Alternate Mobile', name: 'alternatePhone', type: 'tel', required: true },
    { label: 'Email ID', name: 'email', type: 'email', required: true },
    { label: 'Password', name: 'candidatePassword', type: 'password' },
    { label: 'Location', name: 'location', required: true },
    { label: 'Total Experience', name: 'experience', type: 'select', options: experienceOptions, required: true },
    { label: 'Relevant Experience', name: 'relevantExperience', type: 'select', options: experienceOptions.filter((experience) => experience !== 'Fresher'), required: true },
    { label: 'Current Skill', name: 'currentSkill', required: true },
    { label: 'New Skill', name: 'newSkill', required: true },
    { label: 'Last Working Day', name: 'lastWorkingDay', type: 'date', required: true },
    { label: 'Notice Period', name: 'noticePeriod', required: true },
    { label: 'Current Company Name', name: 'currentCompany', required: true },
    { label: 'Previous Companies Name', name: 'previousCompanies', required: true },
    { label: 'Current CTC', name: 'currentCtc', required: true },
    { label: 'Expected CTC', name: 'expectedCtc', required: true },
    { label: 'Preferred Location', name: 'preferredLocation', required: true },
    { label: 'Employment Gaps', name: 'employmentGaps', required: true },
    { label: 'Resume', name: 'resume', type: 'file', accept: '.pdf,.doc,.docx', responseKey: 'resume_file', required: true },
    { label: 'Aadhaar', name: 'aadhaarCard', type: 'file', accept: '.pdf,.jpg,.jpeg,.png', responseKey: 'aadhaar_card_file', required: true },
    { label: 'PAN Card', name: 'panCard', type: 'file', accept: '.pdf,.jpg,.jpeg,.png', responseKey: 'pan_card_file', required: true },
    { label: 'UAN No', name: 'uanNumber', required: true },
    { label: 'UAN Passbook', name: 'uanPassbook', type: 'file', accept: '.pdf,.jpg,.jpeg,.png', responseKey: 'uan_passbook_file', required: true },
    { label: 'Comments', name: 'comments', type: 'textarea' },
    { label: 'Consultant Name', name: 'consultantName', required: true },
  ],
  'Technical Job Support': [
    { label: 'Name', name: 'fullName', required: true },
    { label: 'Mobile', name: 'phone', type: 'tel', required: true },
    { label: 'Alternate Mobile', name: 'alternatePhone', type: 'tel', required: true },
    { label: 'Email ID', name: 'email', type: 'email', required: true },
    { label: 'Password', name: 'candidatePassword', type: 'password' },
    { label: 'Location', name: 'location', required: true },
    { label: 'Total Experience', name: 'experience', type: 'select', options: experienceOptions, required: true },
    { label: 'Role', name: 'role', required: true },
    { label: 'Job Description', name: 'jobDescription', type: 'textarea', required: true },
    { label: 'Company Name', name: 'companyName', required: true },
    { label: 'Timings', name: 'timings', required: true },
    { label: 'Duration', name: 'duration', options: ['1 Month', '2 Months', '3 Months'], type: 'select', required: true },
    { label: 'Consultant Name', name: 'consultantName', required: true },
  ],
  'Interview Support': [
    { label: 'Name', name: 'fullName', required: true },
    { label: 'Mobile', name: 'phone', type: 'tel', required: true },
    { label: 'Alternate Mobile', name: 'alternatePhone', type: 'tel', required: true },
    { label: 'Email ID', name: 'email', type: 'email', required: true },
    { label: 'Password', name: 'candidatePassword', type: 'password', required: true },
    { label: 'Role', name: 'role', required: true },
    { label: 'Job Description', name: 'jobDescription', type: 'textarea', required: true },
    { label: 'Total Experience', name: 'experience', type: 'select', options: experienceOptions, required: true },
    { label: 'Relevant Experience', name: 'relevantExperience', type: 'select', options: experienceOptions.filter((experience) => experience !== 'Fresher'), required: true },
    { label: 'Interview Date', name: 'interviewDate', type: 'date', required: true },
    { label: 'Interview Time', name: 'interviewTime', type: 'time', required: true },
    { label: 'Comments', name: 'comments', type: 'textarea' },
    { label: 'Company Name', name: 'companyName', required: true },
    { label: 'Location', name: 'location', required: true },
    { label: 'Consultant Name', name: 'consultantName', required: true },
  ],
  'Certification Support': [
    { label: 'Name', name: 'fullName', required: true },
    { label: 'Mobile', name: 'phone', type: 'tel', required: true },
    { label: 'Alternate Mobile', name: 'alternatePhone', type: 'tel', required: true },
    { label: 'Email ID', name: 'email', type: 'email', required: true },
    { label: 'Exam Name', name: 'examName', required: true },
    { label: 'Exam Code', name: 'examCode', required: true },
    { label: 'Exam Date', name: 'examDate', type: 'date', required: true },
    { label: 'Exam Time', name: 'examTime', type: 'time', required: true },
    { label: 'Consultant Name', name: 'consultantName', required: true },
  ],
  'Training Support': [
    { label: 'Name', name: 'fullName', required: true },
    { label: 'Mobile', name: 'phone', type: 'tel', required: true },
    { label: 'Alternate Mobile', name: 'alternatePhone', type: 'tel', required: true },
    { label: 'Email ID', name: 'email', type: 'email', required: true },
    { label: 'Year Of Passed Out', name: 'yearPassedOut', type: 'select', options: passoutYears, required: true },
    { label: 'Skill', name: 'skillsTrained', required: true },
    { label: 'Location', name: 'location', required: true },
    { label: 'Aadhaar', name: 'aadhaarCard', type: 'file', accept: '.pdf,.jpg,.jpeg,.png', responseKey: 'aadhaar_card_file', required: true },
    { label: 'Consultant Name', name: 'consultantName', required: true },
  ],
  'Internship Support': [
    { label: 'Name', name: 'fullName', required: true },
    { label: 'Mobile', name: 'phone', type: 'tel', required: true },
    { label: 'Alternate Mobile', name: 'alternatePhone', type: 'tel', required: true },
    { label: 'Email ID', name: 'email', type: 'email', required: true },
    { label: 'Skill', name: 'skillsTrained', required: true },
    { label: 'Location', name: 'location', required: true },
    { label: 'Year Of Passed Out', name: 'yearPassedOut', type: 'select', options: passoutYears, required: true },
    { label: 'Cheque / Bank Passbook', name: 'chequeBankPassbook', type: 'file', accept: '.pdf,.jpg,.jpeg,.png', responseKey: 'cheque_bank_passbook_file', required: true },
    { label: 'Intern Start Date', name: 'internStartDate', type: 'date', required: true },
    { label: 'Intern End Date', name: 'internEndDate', type: 'date', required: true },
    { label: 'Consultant Name', name: 'consultantName', required: true },
  ],
  'Payroll Support': [
    { label: 'Name', name: 'fullName', required: true },
    { label: 'Mobile', name: 'phone', type: 'tel', required: true },
    { label: 'Alternate Mobile', name: 'alternatePhone', type: 'tel', required: true },
    { label: 'Email ID', name: 'email', type: 'email', required: true },
    { label: 'Total Experience', name: 'experience', type: 'select', options: experienceOptions, required: true },
    { label: 'Location', name: 'location', required: true },
    { label: 'Aadhaar', name: 'aadhaarCard', type: 'file', accept: '.pdf,.jpg,.jpeg,.png', responseKey: 'aadhaar_card_file', required: true },
    { label: 'Cheque / Bank Passbook', name: 'chequeBankPassbook', type: 'file', accept: '.pdf,.jpg,.jpeg,.png', responseKey: 'cheque_bank_passbook_file', required: true },
    { label: 'UAN Number', name: 'uanNumber', required: true },
    { label: 'Payroll Start Date', name: 'payrollStartDate', type: 'date', required: true },
    { label: 'Payroll End Date', name: 'payrollEndDate', type: 'date', required: true },
    { label: 'Consultant Name', name: 'consultantName', required: true },
  ],
  'Documentation Support': [
    { label: 'Name', name: 'fullName', required: true },
    { label: 'Mobile', name: 'phone', type: 'tel', required: true },
    { label: 'Alternate Mobile', name: 'alternatePhone', type: 'tel' },
    { label: 'Mail ID', name: 'email', type: 'email', required: true },
    { label: 'PAN Number', name: 'panNumber', required: true },
    { label: 'Date of Birth', name: 'dateOfBirth', type: 'date', required: true },
    { label: 'Permanent Address', name: 'permanentAddress', type: 'textarea', required: true },
    { label: 'Gender', name: 'gender', type: 'select', options: ['Male', 'Female', 'Other', 'Prefer not to say'], required: true },
    { label: 'Qualification', name: 'qualification', required: true },
    { label: 'Offer Date', name: 'offerDate', type: 'date', required: true },
    { label: 'Joining Date', name: 'joiningDate', type: 'date', required: true },
    { label: 'Hike Date', name: 'hikeDate', type: 'date' },
    { label: 'Joining CTC', name: 'joiningCtc', required: true },
    { label: 'Current CTC', name: 'currentCtc', required: true },
    { label: 'Joining Designation', name: 'joiningDesignation', required: true },
    { label: 'Current Designation', name: 'currentDesignation', required: true },
    { label: 'Resignation Date', name: 'resignationDate', type: 'date' },
    { label: 'Relieving Date', name: 'relievingDate', type: 'date' },
    { label: 'Technology / Course Name', name: 'technologyCourseName', required: true },
    { label: 'Bank Name', name: 'bankName', required: true },
    { label: 'Bank Account Number', name: 'bankAccountNumber', required: true },
    { label: 'UAN No', name: 'uanNumber', required: true },
    { label: 'Aadhaar Attachment', name: 'aadhaarCard', type: 'file', accept: '.pdf,.jpg,.jpeg,.png', responseKey: 'aadhaar_card_file', required: true },
    { label: 'PAN Card Attachment', name: 'panCard', type: 'file', accept: '.pdf,.jpg,.jpeg,.png', responseKey: 'pan_card_file', required: true },
    { label: 'Bank 3 Months Statement', name: 'bankStatement', type: 'file', accept: '.pdf,.jpg,.jpeg,.png', responseKey: 'bank_statement_file', required: true },
    { label: 'Consultant Name', name: 'consultantName', required: true },
  ],
};

export default function ApplicationForm({ selectedService = '' }) {
  const [form, setForm] = useState({ ...initialForm, service: selectedService });
  const [files, setFiles] = useState({ aadhaarCard: null, panCard: null, resume: null, uanPassbook: null, chequeBankPassbook: null, bankStatement: null });
  const [submitted, setSubmitted] = useState(false);
  const [editing, setEditing] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [responseData, setResponseData] = useState(null);

  useEffect(() => {
    setForm((current) => ({
      ...current,
      service: selectedService || current.service || '',
    }));
    setSubmitted(false);
    setEditing(false);
    setReviewing(false);
    setError('');
  }, [selectedService]);

  const currentService = services.find((service) => service.title === form.service);
  const isLateralApplication = form.service === 'Lateral Job Support';
  const currentSupportFields = supportFormFields[form.service] || null;
  const formDetails = serviceFormDetails[form.service] || {
    heading: 'Candidate Application Form',
    intro: 'Select a support service and share your details. The team will review your requirement and contact you.',
  };
  const selectedPostGraduationCourses = postGraduationCourses[form.postGraduationType] || [];
  const selectedGraduationCourses = graduationCourses[form.graduationType] || [];

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => {
      if (name === 'graduationType') {
        return {
          ...current,
          graduationType: value,
          graduationCourse: '',
          graduationPassoutYear: '',
          graduationScore: '',
          graduation: value,
        };
      }

      if (name === 'hasPostGraduation') {
        return {
          ...current,
          hasPostGraduation: value,
          postGraduationType: '',
          postGraduationCourse: '',
          postGraduationPassoutYear: '',
          postGraduationScore: '',
        };
      }

      if (name === 'postGraduationType') {
        return {
          ...current,
          postGraduationType: value,
          postGraduationCourse: '',
          postGraduationPassoutYear: '',
          postGraduationScore: '',
        };
      }

      if (name === 'postGraduationCourse') {
        return {
          ...current,
          postGraduationCourse: value,
          postGraduationPassoutYear: '',
          postGraduationScore: '',
        };
      }

      if (name === 'graduationCourse') {
        return {
          ...current,
          graduationCourse: value,
          graduationPassoutYear: '',
          graduationScore: '',
          graduation: buildEducationValue([current.graduationType, value]),
        };
      }

      if (name === 'twelfthType') {
        return {
          ...current,
          twelfthType: value,
          twelfthPassoutYear: '',
          twelfthScore: '',
          twelfthStandard: value,
        };
      }

      return { ...current, [name]: value };
    });
  };

  const handleFileChange = (event) => {
    const { name, files: selectedFiles } = event.target;
    setFiles((current) => ({ ...current, [name]: selectedFiles[0] || null }));
  };

  const getSubmissionPayload = () => {
    const payload = new FormData();
    const values = {
      full_name: form.fullName,
      email: form.email,
      phone_number: form.phone,
      alternate_phone_number: form.alternatePhone,
      candidate_password: form.candidatePassword,
      service_interest: form.service,
      total_experience: form.experience,
      relevant_experience: form.relevantExperience,
      current_skill: form.currentSkill,
      new_skill: form.newSkill,
      current_location: form.location,
      last_working_day: form.lastWorkingDay,
      notice_period: form.noticePeriod,
      current_company: form.currentCompany,
      previous_companies: form.previousCompanies,
      current_ctc: form.currentCtc,
      expected_ctc: form.expectedCtc,
      preferred_location: form.preferredLocation,
      employment_gaps: form.employmentGaps,
      uan_number: form.uanNumber,
      comments: form.comments,
      consultant_name: form.consultantName,
      role: form.role,
      job_description: form.jobDescription,
      company_name: form.companyName,
      timings: form.timings,
      duration: form.duration,
      exam_name: form.examName,
      exam_code: form.examCode,
      exam_date: form.examDate,
      exam_time: form.examTime,
      interview_date: form.interviewDate,
      interview_time: form.interviewTime,
      year_passed_out: form.yearPassedOut,
      intern_start_date: form.internStartDate,
      intern_end_date: form.internEndDate,
      payroll_start_date: form.payrollStartDate,
      payroll_end_date: form.payrollEndDate,
      pan_number: form.panNumber,
      date_of_birth: form.dateOfBirth,
      permanent_address: form.permanentAddress,
      gender: form.gender,
      qualification: form.qualification,
      offer_date: form.offerDate,
      joining_date: form.joiningDate,
      hike_date: form.hikeDate,
      joining_ctc: form.joiningCtc,
      joining_designation: form.joiningDesignation,
      current_designation: form.currentDesignation,
      resignation_date: form.resignationDate,
      relieving_date: form.relievingDate,
      technology_course_name: form.technologyCourseName,
      bank_name: form.bankName,
      bank_account_number: form.bankAccountNumber,
      post_graduation: form.hasPostGraduation === 'No'
        ? 'No'
        : buildEducationValue([
          form.postGraduationType,
          form.postGraduationCourse,
          form.postGraduationPassoutYear ? `Passout ${form.postGraduationPassoutYear}` : '',
          form.postGraduationScore ? `CGPA/Percentage ${form.postGraduationScore}` : '',
        ]),
      graduation: buildEducationValue([
        form.graduationType,
        form.graduationCourse,
        form.graduationPassoutYear ? `Passout ${form.graduationPassoutYear}` : '',
        form.graduationScore ? `CGPA/Percentage ${form.graduationScore}` : '',
      ]),
      twelfth_standard: buildEducationValue([
        form.twelfthType,
        form.twelfthPassoutYear ? `Passout ${form.twelfthPassoutYear}` : '',
        form.twelfthScore ? `CGPA/Percentage ${form.twelfthScore}` : '',
      ]),
      ssc: buildEducationValue([
        form.sscPassoutYear ? `Passout ${form.sscPassoutYear}` : '',
        form.sscScore ? `CGPA/Percentage ${form.sscScore}` : '',
      ]),
      education_gaps: form.educationGaps,
      skills_trained: form.skillsTrained,
    };

    Object.entries(values).forEach(([key, value]) => {
      payload.append(key, value || '');
    });

    if (files.aadhaarCard) {
      payload.append('aadhaar_card', files.aadhaarCard);
    }
    if (files.panCard) {
      payload.append('pan_card', files.panCard);
    }
    if (files.resume) {
      payload.append('resume', files.resume);
    }
    if (files.uanPassbook) {
      payload.append('uan_passbook', files.uanPassbook);
    }
    if (files.chequeBankPassbook) {
      payload.append('cheque_bank_passbook', files.chequeBankPassbook);
    }
    if (files.bankStatement) {
      payload.append('bank_statement', files.bankStatement);
    }

    return payload;
  };

  const renderSupportField = (field) => {
    const label = `${field.label}${field.required ? ' *' : ''}`;

    if (field.type === 'file') {
      return (
        <div key={field.name} className="form-group">
          <label>{label}</label>
          <input
            type="file"
            name={field.name}
            onChange={handleFileChange}
            accept={field.accept}
            required={field.required && !responseData?.[field.responseKey]}
          />
          {responseData?.[field.responseKey] ? (
            <p className="application-file-note">Current file uploaded. Choose a new file only if you want to replace it.</p>
          ) : null}
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.name} className="form-group">
          <label>{label}</label>
          <select name={field.name} value={form[field.name]} onChange={handleChange} required={field.required}>
            <option value="">Select {field.label.toLowerCase()}</option>
            {field.options.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div key={field.name} className="form-group">
          <label>{label}</label>
          <textarea name={field.name} value={form[field.name]} onChange={handleChange} required={field.required} />
        </div>
      );
    }

    return (
      <div key={field.name} className="form-group">
        <label>{label}</label>
        <input
          type={field.type || 'text'}
          name={field.name}
          value={form[field.name]}
          onChange={handleChange}
          required={field.required}
        />
      </div>
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const isEditing = editing && responseData?.id;
      const response = await fetch(
        isEditing ? `${apiBaseUrl}/applications/${responseData.id}/` : `${apiBaseUrl}/applications/`,
        {
          method: 'POST',
          body: getSubmissionPayload(),
        }
      );

      const result = await parseApiResponse(response);

      if (!response.ok) {
        throw new Error(result.detail || 'Could not submit the application.');
      }

      setResponseData(result);
      setSubmitted(true);
      setEditing(false);
      setReviewing(false);
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="application-section">
      <div className="container">
        <div className="application-shell">
          <div className="application-topbar">
            <div>
              <p className="section-label">Apply Now</p>
              <h2 className="section-title">
                {currentService ? currentService.title : 'Choose Support'}
              </h2>
              <p className="application-copy">{formDetails.intro}</p>
            </div>

            {currentService ? (
              <div className="application-selected-pill">
                <span>{currentService.num}</span>
                <strong>{currentService.title}</strong>
              </div>
            ) : null}
          </div>

          <div className="application-form-wrap">
            {!submitted || editing ? (
              <>
                <h3 className="application-form-title">{formDetails.heading}</h3>
                <p className="application-form-subtitle">
                  {editing ? 'Update the details below. This will edit the same application record.' : 'The selected service is attached to this application automatically.'}
                </p>

                {error ? <div className="application-error">{error}</div> : null}

                <form onSubmit={handleSubmit} className="application-form">
                  {currentSupportFields ? (
                    <div className="application-form-block">
                      <div className="application-form-block-head">
                        <span>01</span>
                        <h4>{form.service.replace(' Support', '')} Details</h4>
                      </div>
                      <div className="application-form-row">
                        <div className="form-group">
                          <label>Service Required *</label>
                          <select name="service" value={form.service} onChange={handleChange} required>
                            <option value="">Select a service</option>
                            {services.map((service) => (
                              <option key={service.num} value={service.title}>{service.title}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="application-form-row">
                        {currentSupportFields.map(renderSupportField)}
                      </div>
                    </div>
                  ) : null}

                  {!currentSupportFields ? (
                    <>
                  <div className="application-form-block">
                    <div className="application-form-block-head">
                      <span>01</span>
                      <h4>Candidate Details</h4>
                    </div>

                    <div className="application-form-row">
                      <div className="form-group">
                        <label>Full Name *</label>
                        <input
                          name="fullName"
                          value={form.fullName}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Mobile *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="application-form-row">
                      <div className="form-group">
                        <label>Alternate Mobile *</label>
                        <input
                          type="tel"
                          name="alternatePhone"
                          value={form.alternatePhone}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Mail ID *</label>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Location{isLateralApplication ? ' *' : ''}</label>
                        <input
                          name="location"
                          value={form.location}
                          onChange={handleChange}
                          required={isLateralApplication}
                        />
                      </div>
                    </div>

                    {isLateralApplication ? (
                      <div className="application-form-row">
                        <div className="form-group">
                          <label>Password</label>
                          <input
                            type="password"
                            name="candidatePassword"
                            value={form.candidatePassword}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    ) : null}

                    <div className="application-form-row">
                      <div className="form-group">
                        <label>Service Required *</label>
                        <select name="service" value={form.service} onChange={handleChange} required>
                          <option value="">Select a service</option>
                          {services.map((service) => (
                            <option key={service.num} value={service.title}>{service.title}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Total Experience{isLateralApplication ? ' *' : ''}</label>
                        <select
                          name="experience"
                          value={form.experience}
                          onChange={handleChange}
                          required={isLateralApplication}
                        >
                          <option value="">Select experience</option>
                          {experienceOptions.map((experience) => (
                            <option key={experience} value={experience}>{experience}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {isLateralApplication ? (
                    <div className="application-form-block">
                      <div className="application-form-block-head">
                        <span>02</span>
                        <h4>Lateral Details</h4>
                      </div>

                      <div className="application-form-row">
                        <div className="form-group">
                          <label>Relevant Experience *</label>
                          <select
                            name="relevantExperience"
                            value={form.relevantExperience}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select relevant experience</option>
                            {experienceOptions.filter((experience) => experience !== 'Fresher').map((experience) => (
                              <option key={experience} value={experience}>{experience}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Skill *</label>
                          <input
                            name="skillsTrained"
                            value={form.skillsTrained}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="application-form-row">
                        <div className="form-group">
                          <label>Last Working Day *</label>
                          <input
                            type="date"
                            name="lastWorkingDay"
                            value={form.lastWorkingDay}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Notice Period *</label>
                          <input
                            name="noticePeriod"
                            value={form.noticePeriod}
                            onChange={handleChange}
                            placeholder="Immediate / 30 days / 60 days"
                            required
                          />
                        </div>
                      </div>

                      <div className="application-form-row">
                        <div className="form-group">
                          <label>Current Company Name *</label>
                          <input
                            name="currentCompany"
                            value={form.currentCompany}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Previous Companies Name *</label>
                          <input
                            name="previousCompanies"
                            value={form.previousCompanies}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="application-form-row">
                        <div className="form-group">
                          <label>Current CTC *</label>
                          <input
                            name="currentCtc"
                            value={form.currentCtc}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Expected CTC *</label>
                          <input
                            name="expectedCtc"
                            value={form.expectedCtc}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="application-form-row">
                        <div className="form-group">
                          <label>Preferred Location *</label>
                          <input
                            name="preferredLocation"
                            value={form.preferredLocation}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Employment Gaps *</label>
                          <input
                            name="employmentGaps"
                            value={form.employmentGaps}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="application-form-row">
                        <div className="form-group">
                          <label>UAN No *</label>
                          <input
                            name="uanNumber"
                            value={form.uanNumber}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>UAN Passbook *</label>
                          <input
                            type="file"
                            name="uanPassbook"
                            onChange={handleFileChange}
                            accept=".pdf,.jpg,.jpeg,.png"
                            required={!responseData?.uan_passbook_file}
                          />
                          {responseData?.uan_passbook_file ? (
                            <p className="application-file-note">Current file uploaded. Choose a new file only if you want to replace it.</p>
                          ) : null}
                        </div>
                      </div>

                      <div className="application-form-row">
                        <div className="form-group">
                          <label>Consultant Name *</label>
                          <input
                            name="consultantName"
                            value={form.consultantName}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Comments</label>
                          <textarea
                            name="comments"
                            value={form.comments}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div className="application-form-block">
                    <div className="application-form-block-head">
                      <span>{isLateralApplication ? '03' : '02'}</span>
                      <h4>Education Details</h4>
                    </div>

                    <div className="application-form-row application-form-row-single">
                      <div className="form-group">
                        <label>Post Graduation</label>
                        <div className="application-radio-group">
                          {['Yes', 'No'].map((option) => (
                            <label
                              key={option}
                              className={`application-radio-option ${form.hasPostGraduation === option ? 'active' : ''}`}
                            >
                              <input
                                type="radio"
                                name="hasPostGraduation"
                                value={option}
                                checked={form.hasPostGraduation === option}
                                onChange={handleChange}
                              />
                              <span>{option}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    {form.hasPostGraduation === 'Yes' ? (
                      <>
                        <div className="application-form-row application-form-row-compact">
                          <div className="form-group">
                            <label>Post Graduation Type</label>
                            <select
                              name="postGraduationType"
                              value={form.postGraduationType}
                              onChange={handleChange}
                            >
                              <option value="">Select post graduation type</option>
                              {Object.keys(postGraduationCourses).map((type) => (
                                <option key={type} value={type}>{type}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Post Graduation Course</label>
                            <select
                              name="postGraduationCourse"
                              value={form.postGraduationCourse}
                              onChange={handleChange}
                              disabled={!form.postGraduationType}
                            >
                              <option value="">Select course</option>
                              {selectedPostGraduationCourses.map((course) => (
                                <option key={course} value={course}>{course}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {form.postGraduationCourse ? (
                          <div className="application-form-row application-form-row-compact">
                            <div className="form-group">
                              <label>Post Graduation Passout Year</label>
                              <select
                                name="postGraduationPassoutYear"
                                value={form.postGraduationPassoutYear}
                                onChange={handleChange}
                              >
                                <option value="">Select year</option>
                                {passoutYears.map((year) => (
                                  <option key={year} value={year}>{year}</option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Post Graduation CGPA / Percentage</label>
                              <input
                                name="postGraduationScore"
                                value={form.postGraduationScore}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        ) : null}
                      </>
                    ) : null}

                    <div className="application-education-label">Graduation</div>

                    <div className="application-form-row">
                      <div className="form-group">
                        <label>Graduation Type</label>
                        <select
                          name="graduationType"
                          value={form.graduationType}
                          onChange={handleChange}
                        >
                          <option value="">Select graduation type</option>
                          {Object.keys(graduationCourses).map((type) => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {form.graduationType ? (
                      <div className="application-form-row application-form-row-single">
                        <div className="form-group">
                          <label>Graduation Course</label>
                          <select
                            name="graduationCourse"
                            value={form.graduationCourse}
                            onChange={handleChange}
                          >
                            <option value="">Select course</option>
                            {selectedGraduationCourses.map((course) => (
                              <option key={course} value={course}>{course}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ) : null}

                    {form.graduationCourse ? (
                      <div className="application-form-row application-form-row-compact">
                        <div className="form-group">
                          <label>Graduation Passout Year</label>
                          <select
                            name="graduationPassoutYear"
                            value={form.graduationPassoutYear}
                            onChange={handleChange}
                          >
                            <option value="">Select year</option>
                            {passoutYears.map((year) => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Graduation CGPA / Percentage</label>
                          <input
                            name="graduationScore"
                            value={form.graduationScore}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    ) : null}

                    <div className="application-form-row">
                      <div className="form-group">
                        <label>12th / Diploma Type</label>
                        <select
                          name="twelfthType"
                          value={form.twelfthType}
                          onChange={handleChange}
                        >
                          <option value="">Select type</option>
                          {twelfthOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {form.twelfthType ? (
                      <div className="application-form-row application-form-row-compact">
                        <div className="form-group">
                          <label>12th / Diploma Passout Year</label>
                          <select
                            name="twelfthPassoutYear"
                            value={form.twelfthPassoutYear}
                            onChange={handleChange}
                          >
                            <option value="">Select year</option>
                            {passoutYears.map((year) => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>12th / Diploma CGPA / Percentage</label>
                          <input
                            name="twelfthScore"
                            value={form.twelfthScore}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    ) : null}

                    <div className="application-form-row">
                      <div className="form-group">
                        <label>SSC Passout Year</label>
                        <select
                          name="sscPassoutYear"
                          value={form.sscPassoutYear}
                          onChange={handleChange}
                        >
                          <option value="">Select year</option>
                          {passoutYears.map((year) => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>SSC CGPA / Percentage</label>
                        <input
                          name="sscScore"
                          value={form.sscScore}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="application-form-block">
                    <div className="application-form-block-head">
                      <span>{isLateralApplication ? '04' : '03'}</span>
                      <h4>Documents</h4>
                    </div>

                    {!isLateralApplication ? (
                      <div className="application-form-row">
                        <div className="form-group">
                          <label>Gaps In Education</label>
                          <input
                            name="educationGaps"
                            value={form.educationGaps}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Skills Trained</label>
                          <input
                            name="skillsTrained"
                            value={form.skillsTrained}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    ) : null}

                    <div className="application-form-row">
                      <div className="form-group">
                        <label>Aadhaar Card *</label>
                        <input
                          type="file"
                          name="aadhaarCard"
                          onChange={handleFileChange}
                          accept=".pdf,.jpg,.jpeg,.png"
                          required={!responseData?.aadhaar_card_file}
                        />
                        {responseData?.aadhaar_card_file ? (
                          <p className="application-file-note">Current file uploaded. Choose a new file only if you want to replace it.</p>
                        ) : null}
                      </div>
                      <div className="form-group">
                        <label>PAN Card *</label>
                        <input
                          type="file"
                          name="panCard"
                          onChange={handleFileChange}
                          accept=".pdf,.jpg,.jpeg,.png"
                          required={!responseData?.pan_card_file}
                        />
                        {responseData?.pan_card_file ? (
                          <p className="application-file-note">Current file uploaded. Choose a new file only if you want to replace it.</p>
                        ) : null}
                      </div>
                    </div>

                    <div className="application-form-row application-form-row-single">
                      <div className="form-group">
                        <label>Resume *</label>
                        <input
                          type="file"
                          name="resume"
                          onChange={handleFileChange}
                          accept=".pdf,.doc,.docx"
                          required={!responseData?.resume_file}
                        />
                        {responseData?.resume_file ? (
                          <p className="application-file-note">Current file uploaded. Choose a new file only if you want to replace it.</p>
                        ) : null}
                      </div>
                    </div>

                    {!isLateralApplication ? (
                      <div className="application-form-row application-form-row-single">
                        <div className="form-group">
                          <label>Consultant Name *</label>
                          <input
                            name="consultantName"
                            value={form.consultantName}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                    </>
                  ) : null}

                  <button type="submit" className="application-submit" disabled={submitting}>
                    {submitting ? 'Submitting...' : editing ? 'Update Application' : 'Submit Application'}
                  </button>
                </form>
              </>
            ) : (
              <>
                {!reviewing ? (
                  <div className="application-success">
                    <div className="application-success-icon">Sent</div>
                    <h3>{responseData?.message?.includes('edited') ? 'Application Edited' : 'Application Submitted'}</h3>
                    <p>
                      Your request for <strong>{responseData?.service_interest || form.service || 'career support'}</strong> has been saved successfully.
                    </p>
                    <div className="application-action-row">
                      <button type="button" className="application-secondary-link" onClick={() => setReviewing(true)}>
                        Review Submission
                      </button>
                      <button type="button" className="application-secondary-link application-edit-link" onClick={() => setEditing(true)}>
                        Edit
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="application-review">
                    <h3>Submission Details</h3>
                    <div className="application-review-grid">
                      {[
                        ['Application ID', responseData?.fjs_id],
                        ['Full Name', responseData?.full_name],
                        ['Mobile', responseData?.phone_number],
                        ['Alternate Mobile', responseData?.alternate_phone_number],
                        ['Email', responseData?.email],
                        ['Service', responseData?.service_interest || responseData?.job],
                        ['Experience', responseData?.total_experience],
                        ['Relevant Experience', responseData?.relevant_experience],
                        ['Location', responseData?.current_location],
                        ['Last Working Day', responseData?.last_working_day],
                        ['Notice Period', responseData?.notice_period],
                        ['PAN Number', responseData?.pan_number],
                        ['Date of Birth', responseData?.date_of_birth],
                        ['Permanent Address', responseData?.permanent_address],
                        ['Gender', responseData?.gender],
                        ['Qualification', responseData?.qualification],
                        ['Offer Date', responseData?.offer_date],
                        ['Joining Date', responseData?.joining_date],
                        ['Hike Date', responseData?.hike_date],
                        ['Joining CTC', responseData?.joining_ctc],
                        ['Current Company', responseData?.current_company],
                        ['Previous Companies', responseData?.previous_companies],
                        ['Current CTC', responseData?.current_ctc],
                        ['Joining Designation', responseData?.joining_designation],
                        ['Current Designation', responseData?.current_designation],
                        ['Resignation Date', responseData?.resignation_date],
                        ['Relieving Date', responseData?.relieving_date],
                        ['Technology / Course Name', responseData?.technology_course_name],
                        ['Bank Name', responseData?.bank_name],
                        ['Bank Account Number', responseData?.bank_account_number],
                        ['Expected CTC', responseData?.expected_ctc],
                        ['Preferred Location', responseData?.preferred_location],
                        ['Post Graduation', responseData?.post_graduation],
                        ['Graduation', responseData?.graduation],
                        ['12th / Diploma', responseData?.twelfth_standard],
                        ['SSC', responseData?.ssc],
                        ['Education Gaps', responseData?.education_gaps],
                        ['Employment Gaps', responseData?.employment_gaps],
                        ['Skills Trained', responseData?.skills_trained],
                        ['UAN No', responseData?.uan_number],
                        ['Comments', responseData?.comments],
                        ['Consultant Name', responseData?.consultant_name],
                        ['Aadhaar Card', responseData?.aadhaar_card_file ? 'Uploaded' : 'Not uploaded'],
                        ['PAN Card', responseData?.pan_card_file ? 'Uploaded' : 'Not uploaded'],
                        ['Resume', responseData?.resume_file ? 'Uploaded' : 'Not uploaded'],
                        ['UAN Passbook', responseData?.uan_passbook_file ? 'Uploaded' : 'Not uploaded'],
                        ['Bank Statement', responseData?.bank_statement_file ? 'Uploaded' : 'Not uploaded'],
                      ].map(([label, value]) => (
                        <div key={label} className="application-review-item">
                          <span>{label}</span>
                          <strong>{value || '-'}</strong>
                        </div>
                      ))}
                    </div>
                    <div className="application-action-row">
                      <button type="button" className="application-secondary-link" onClick={() => setReviewing(false)}>
                        Back
                      </button>
                      <button type="button" className="application-secondary-link application-edit-link" onClick={() => {
                        setReviewing(false);
                        setEditing(true);
                      }}>
                        Edit
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
